import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import api, { fileUrl } from "@/lib/api";
import { Star, ShieldCheck, ChefHat, Share2, X } from "lucide-react";
import { toast } from "sonner";

export default function CompareChefs() {
  const [params, setParams] = useSearchParams();
  const ids = (params.get("chefs") || "").split(",").map((s) => s.trim()).filter(Boolean);
  const [chefs, setChefs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const out = await Promise.all(ids.map((id) =>
        api.get(`/chefs/${id}`).then((r) => r.data).catch(() => null)
      ));
      setChefs(out.filter(Boolean));
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get("chefs")]);

  const remove = (id) => {
    const next = ids.filter((x) => x !== id);
    if (next.length) setParams({ chefs: next.join(",") });
    else setParams({});
  };

  const share = async () => {
    if (ids.length === 0) return;
    const url = `${window.location.origin}/compare-chefs?chefs=${ids.join(",")}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Comparison link copied");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="tick-label">Compare</div>
            <h1 className="font-display font-black text-5xl tracking-tighter mt-2">Chef side-by-side.</h1>
          </div>
          {ids.length > 0 && (
            <button data-testid="share-chefs-btn" onClick={share} className="btn-outline inline-flex items-center gap-2">
              <Share2 size={13} /> Share this comparison
            </button>
          )}
        </div>

        {ids.length === 0 ? (
          <div className="mt-14 text-center border hairline py-16">
            <div className="font-display text-2xl font-bold">No chefs selected.</div>
            <p className="text-neutral-500 mt-3">Open a chef profile and copy the URL, or share a link with <code className="font-mono">?chefs=id1,id2,id3</code>.</p>
            <Link to="/find-a-chef" className="btn-signal inline-block mt-6">Find chefs</Link>
          </div>
        ) : loading ? (
          <div className="mt-14 tick-label">Loading…</div>
        ) : (
          <div data-testid="chef-compare-grid" className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {chefs.map((c) => (
              <div key={c.id} data-testid={`compare-chef-${c.id}`} className="border hairline p-5">
                <div className="flex items-start gap-3">
                  <div className="w-16 h-16 border hairline bg-neutral-900 overflow-hidden flex-shrink-0">
                    {c.avatar_url ? (
                      <img src={fileUrl(c.avatar_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><ChefHat size={22} className="text-neutral-700" /></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <Link to={`/chef/${c.id}`} className="font-display font-bold text-xl truncate hover:text-[#FF3B30]">{c.name}</Link>
                      {c.license_verified && <ShieldCheck size={13} className="text-[#FF3B30] flex-shrink-0" />}
                    </div>
                    {c.review_count > 0 && (
                      <div className="mt-1 flex items-center gap-1 text-sm">
                        <Star size={12} className="fill-[#FF3B30] text-[#FF3B30]" />
                        <span className="font-mono">{c.rating.toFixed(1)}</span>
                        <span className="text-neutral-500">({c.review_count})</span>
                      </div>
                    )}
                  </div>
                  <button data-testid={`remove-compare-${c.id}`} onClick={() => remove(c.id)} className="text-neutral-500 hover:text-[#FF3B30]"><X size={14} /></button>
                </div>
                {c.bio && <p className="mt-4 text-sm text-neutral-400">{c.bio}</p>}
                {c.specialties?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {c.specialties.map((s, i) => (
                      <span key={i} className="text-xs border hairline px-2 py-0.5">{s}</span>
                    ))}
                  </div>
                )}
                <div className="mt-4 grid grid-cols-2 gap-0 border hairline">
                  <div className="p-2 border-r hairline">
                    <div className="tick-label">Paid gigs</div>
                    <div className="font-mono text-lg font-bold">{c.paid_bookings}</div>
                  </div>
                  <div className="p-2">
                    <div className="tick-label">Portfolio</div>
                    <div className="font-mono text-lg font-bold">{(c.portfolio_photos || []).length}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
